const Constants = {
  GAME_TYPE_DAILY: "daily",
  GAME_TYPE_PRACTICE: "practice",

  // The build's version. The client carries whatever copy was baked into its
  // bundle; the server reads this one live, so the two differ exactly when a
  // player is running a bundle from before the last deploy. That matters
  // because the daily's mechanics live in the bundle -- the server sends the
  // day's shape, but only the client can draw it -- so a tab left open across a
  // deploy can be handed a board it has no code to play. `App.isBundleCurrent`
  // is what compares them, and the footer renders this rather than a literal of
  // its own, so a bump cannot reach players' eyes without reaching the check.
  //
  // Bumped in lockstep with `version` in package.json, in the same commit.
  VERSION: "1.30",

  // The release date shown beside it. Display only -- the handshake compares
  // VERSION alone, so this can be restamped without touching what the check
  // means. It sits here rather than in the footer so the whole of the version
  // line is one thing to edit when a release goes out.
  VERSION_DATE: "2026/10/24",

  // The site's type setting. "modern" is Fraunces/Figtree, the default;
  // "classic" restores Rubik, the setting used until 2026/08.
  TYPOGRAPHY_MODERN: "modern",
  TYPOGRAPHY_CLASSIC: "classic",

  WORD_POSITION_NONE: "none",
  WORD_POSITION_START: "start",
  WORD_POSITION_MIDDLE: "middle",
  WORD_POSITION_END: "end",

  SCORES_TODAY: "today",
  SCORES_YESTERDAY: "yesterday",
  SCORES_THIS_WEEK: "this-week",
  SCORES_LAST_WEEK: "last-week",
  SCORES_THIS_SEASON: "this-season",
  SCORES_LAST_SEASON: "last-season",

  // The two axes a score period sits on: how long a period is, and how far back
  // it is. Every period above is one span crossed with one recency, and the pair
  // is what the two selectors above the board set.
  SCORE_SPAN_DAY: "day",
  SCORE_SPAN_WEEK: "week",
  SCORE_SPAN_SEASON: "season",

  SCORE_RECENCY_THIS: "this",
  SCORE_RECENCY_LAST: "last",

  // A season is a six-week block of dailies, numbered from 1 at the epoch.
  // The epoch is a Monday and 42 days is exactly six leaderboard weeks, so a
  // season boundary always falls on a Monday and no season ever contains a
  // partial week. Season maths lives in Seasons.js.
  SEASON_EPOCH: "2026-09-07",
  SEASON_LENGTH_DAYS: 42,

  // A season total counts a player's best five scores on each day of the week,
  // so each weekday comes round six times and its lowest is dropped -- a missed
  // day counting as the lowest. That forgives one absence per weekday without
  // trimming anybody's best board. Counted per weekday rather than across the
  // season because a Monday and a Sunday are different sizes of board: a
  // season-wide "drop the lowest" would only ever drop a Monday.
  SEASON_SCORES_COUNTED_PER_WEEKDAY: 5,

  // April Fools 2022 was played against a French dictionary, so its scores are
  // not comparable with any other day's. No multi-day total includes it.
  EXCLUDED_SCORE_DATE: "2022-04-01",

  PENALTY_HOLD: -3,

  SCORE_TABLE: [0, 0, 0, 1, 2, 4, 6, 8, 10, 12, 15, 18],

  // The shortest word a line will score. Must agree with SCORE_TABLE, whose
  // first three entries are worth nothing; it is stated separately because the
  // scan has to reject a short word outright rather than score it as zero,
  // which would still light it up on the board as that line's word.
  MINIMUM_WORD_LENGTH: 3,
  // The longest word the rules' score chart lists: a daily's widest line is
  // seven cells with three of them expanded. Free play can reach one more.
  SCORE_CHART_MAX_LENGTH: 10,

  // What a word scores for each bonus cell it runs through, so a word crossing
  // two of them scores four times. The multiplier applies to the word the line
  // already scores -- the longest one -- rather than choosing a different word:
  // a bonus cell is something to route your long word through, not a reason to
  // abandon it.
  BONUS_CELL_MULTIPLIER: 2,

  // The widest a round's choice ever gets. An increment day cycles up to this
  // and back, and its letters are dealt this many to a round however few of
  // them the day is currently showing -- which is why the deal is wider than
  // the display, and why both sides have to agree on how much of a round is
  // actually on offer.
  MAX_LETTER_OPTIONS_PER_ROUND: 3,

  DEFAULT_ROWS: 5,
  DEFAULT_COLUMNS: 5,
  DEFAULT_LETTER_OPTIONS_PER_ROUND: 2,
  DEFAULT_START_LETTER_COUNT: 0,
  DEFAULT_HOLD_COUNT: 0,
  DEFAULT_PREVIEW_COUNT: 0,
  DEFAULT_EXPANSION_COUNT: 0,
  DEFAULT_BONUS_CELL_COUNT: 0,

  // 0 = Sunday, as moment reports it. The schedule tables below are Monday
  // first; Seasons.js converts.
  DAYS: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],

  // What a day is unless its entry says otherwise. Keeping these out of the
  // tables is what lets a plain Monday be one short line, so the ramp from
  // Monday to Sunday is legible at a glance instead of buried in zeroes.
  DAILY_DEFAULTS: {
    letterOptionsPerRound: 2,
    startLetterCount: 0,
    holdCount: 0,
    previewCount: 0,
    expansionCount: 0,
    bonusCellCount: 0,
    adjacencyMode: false,
    incrementMode: false,
    dropMode: false,
    slideMode: false,
    wraparoundMode: false
  },

  // What a spectated board is opened with. The board itself is loaded from the
  // recorded game, so the day's parameters say nothing about it -- a 0x0 grid
  // stands in until the record arrives.
  SPECTATOR_PARAMETERS: {
    rows: 0,
    columns: 0,
    letterOptionsPerRound: 0,
    startLetterCount: 0,
    holdCount: 0,
    previewCount: 0,
    expansionCount: 0,
    bonusCellCount: 0,
    adjacencyMode: false,
    incrementMode: false,
    dropMode: false,
    slideMode: false,
    wraparoundMode: false
  },

  // The schedule that ran before seasons existed, used for every date before
  // SEASON_EPOCH so that history -- and the week leading up to Season 1 -- is
  // still described by the parameters it was actually played under.
  LEGACY_SCHEDULE: [
    { rows: 5, columns: 5, startLetterCount: 1 },
    { rows: 5, columns: 5, startLetterCount: 1, expansionCount: 2 },
    { rows: 5, columns: 6, startLetterCount: 2, previewCount: 1, adjacencyMode: true },
    { rows: 6, columns: 6, startLetterCount: 2, expansionCount: 2, letterOptionsPerRound: 3 },
    { rows: 4, columns: 7, holdCount: 1, dropMode: true },
    { rows: 5, columns: 5, startLetterCount: 1, holdCount: 1, expansionCount: 3, incrementMode: true },
    { rows: 7, columns: 7, startLetterCount: 2, previewCount: 1, holdCount: 1, adjacencyMode: true }
  ],

  // One week of daily parameters per season, Monday first, so each season's
  // ramp -- Monday the smallest and plainest, Sunday the largest and most
  // involved -- reads straight down the block. That ramp is the part players
  // learn once and rely on; what varies between seasons is which twists land
  // where, and how big each day is.
  //
  // Authored by hand rather than generated at runtime: every board a player
  // ever sees has been looked at, and a schedule can only change with a
  // deploy. `Seasons.test.js` fails once the current season runs past the end
  // of this list, or once the next one is within a week and still unwritten.
  SEASONS: [
    // Season 1. There are exactly six twists a day can carry and seven days to
    // put them on, so a week is built as a bijection: Monday does none of them
    // and every other day does exactly one. That is what lets a day be named by
    // the single thing it does differently -- gold day, expansion day, slide
    // day, increment day, drop day, adjacency day -- which is a far easier week
    // to learn than one where twists pile up towards Sunday.
    //
    // Board size no longer runs as a strict ramp. The season is capped at 36
    // cells, so the back half of the week sits at or just under the cap and the
    // twists alone separate those days, and Thursday is deliberately smaller
    // than the day before it. Saturday spends its 35 on a wide 5x7 rather than
    // a square, which gives drop mode seven columns to choose between and keeps
    // the day distinct from the 6x6 either side of it. The cap is on cells, not
    // on letters: Wednesday's expanded cells hold two apiece, so a line there
    // can still run to eight characters where the widest plain line in the week
    // is seven. The twists are ordered by how much they complicate a turn: gold
    // changes only what a line scores, adjacency constrains every placement on
    // the largest board of the week.
    //
    // Previews, start letters, holds and wider choice are not twists and never
    // count as a day's gimmick. They are handed out to offset the day they land
    // on -- three options on the days that take a choice away from you, a
    // preview on the days that most reward seeing what is coming, a hold on the
    // days where the letter you want is worth more a turn later than now. A
    // hold is help with a cost attached rather than a rule change, which is why
    // it can sit on a day that already has a twist: the gold day and the slide
    // day both carry one.
    [
      { rows: 5, columns: 5, startLetterCount: 1 },
      { rows: 5, columns: 5, startLetterCount: 1, bonusCellCount: 2, holdCount: 1 },
      { rows: 5, columns: 6, startLetterCount: 1, expansionCount: 2 },
      { rows: 5, columns: 5, startLetterCount: 1, previewCount: 1, holdCount: 1, slideMode: true },
      { rows: 6, columns: 6, startLetterCount: 1, incrementMode: true },
      { rows: 5, columns: 7, letterOptionsPerRound: 3, dropMode: true },
      { rows: 6, columns: 6, startLetterCount: 2, previewCount: 1, letterOptionsPerRound: 3, adjacencyMode: true }
    ],
    // Season 2 sets slide aside and brings in wraparound: a plain Monday, and
    // one twist on every other day until Sunday, which doubles up. The order
    // is otherwise Season 1's turned over -- the twists that constrain where a
    // letter may go come first, on the smaller boards, where a cramped choice
    // costs less, and the ones that change what a line can hold or score come
    // after, on the boards at the cap -- with adjacency kept back for Sunday,
    // where Season 1 put it, as the week's hardest board.
    //
    // Increment opens the week on a 5x5 with a hold, because its one-option
    // rounds are where a letter worth saving most often turns up. Wednesday's
    // drop is the old 4x7 from before seasons: shallow columns keep a drop from
    // burying a letter four rows under the word it was meant for. Wraparound
    // makes its first appearance midweek on a 5x6 rather than at the end of
    // the week, so a twist nobody has played yet meets a board of moderate
    // size; it adds choices rather than taking them away, so it gets the
    // ordinary two options and nothing to offset it. Friday spends 36 cells on
    // three gold cells rather than Season 1's two, and Saturday's three
    // expanded cells go on a 5x7, so a row can run to ten characters if all
    // three land in it -- past the eight any day has allowed before, and still
    // inside SCORE_TABLE. Sunday is Season 1's adjacency day with
    // Tuesday's increment laid over it, the one day of the week to carry two
    // twists: adjacency narrows which cell a letter may go in, and increment
    // narrows which letter there is to put there. Increment takes the place
    // of Season 1's three options, so its cycle starts at the default two, as
    // Tuesday's does; the preview and the two start letters stay to offset it,
    // and a hold joins them, since a one-option round on a board where only
    // some cells are open is where the letter you were dealt most often has
    // nowhere good to go yet.
    [
      { rows: 5, columns: 5, startLetterCount: 1 },
      { rows: 5, columns: 5, startLetterCount: 1, holdCount: 1, incrementMode: true },
      { rows: 4, columns: 7, letterOptionsPerRound: 3, dropMode: true },
      { rows: 5, columns: 6, startLetterCount: 1, wraparoundMode: true },
      { rows: 6, columns: 6, startLetterCount: 1, bonusCellCount: 3, holdCount: 1 },
      { rows: 5, columns: 7, startLetterCount: 2, expansionCount: 3 },
      { rows: 6, columns: 6, startLetterCount: 2, previewCount: 1, holdCount: 1, adjacencyMode: true, incrementMode: true }
    ]
  ],

  ERROR_PLAYER_NAME_INVALID: "Please enter a name between 3 and 24 characters long.",
  ERROR_DAILY_ATTEMPT_RECORDED: "You've already played today's daily!",
  ERROR_PLAYER_BANNED: "It looks like you've been playing the daily multiple times per day under different names.\nPlease don't do this—it's not fair to players who follow the rules and play just once per day.\nAccess to the daily will be restored in a couple of days.",
  ERROR_MISSING_FIELDS: "Please fill out all mandatory fields.",
  ERROR_MUST_BE_FIRST_PLAY: "Please check the box if this is your first attempt at today's daily.\nIf it isn't, feel free to play Free Play mode!",
  ERROR_NOT_LOGGED_IN: "You must be logged in to play the daily.",
  ERROR_PASSWORDS_DO_NOT_MATCH: "Your passwords do not match.",
  ERROR_TURN_NOT_RECORDED: "That move couldn't be saved, so it hasn't been played.\nTry again, or reload the page to pick up from your last saved move.",
  ERROR_LETTERS_NOT_RECEIVED: "Your letters couldn't be loaded.\nAny moves you've made are saved—reload the page to pick up from them.",
  ERROR_TYPOGRAPHY_NOT_SAVED: "That setting couldn't be saved, so it hasn't been changed.\nPlease try again in a moment.",
  ERROR_VERSION_STALE: "This page is running an old version of the game.\nPlease reload to play today's daily.",
  ERROR_CHAT_TOO_FAST: "You're sending messages too quickly. Please wait a moment.",
  ERROR_CHAT_NOT_SENT: "That message couldn't be sent. Please try again.",
  ERROR_CHAT_NOT_DELETED: "That message couldn't be deleted.",

  ATTEMPT_COMPLETE: "complete",
  ATTEMPT_INCOMPLETE: "incomplete",
  ATTEMPT_NONE: "none",

  MESSAGE_ADJACENCY_MODE: "All letters (except the first) must be placed adjacent to an existing one.",
  MESSAGE_DROP_MODE: "Each placed letter drops to the lowest available cell in its column.",
  MESSAGE_SLIDE_MODE: "Letters slide in from the edges, pushing existing letters when necessary.",
  MESSAGE_WRAPAROUND_MODE: "Words can wrap around the edges of the board.",
  MESSAGE_EXPANDED: "Green cells accept two letters. Words using these cells must use both letters, in the order that they're placed.",
  MESSAGE_BONUS_CELLS: "Gold cells double the score of any word that runs through them. The highest-scoring word in each row and column is selected.",

  MESSAGE_CELL_ALREADY_OCCUPIED: "That cell is already occupied.",
  MESSAGE_CELL_NOT_ADJACENT: "That cell is not adjacent to an occupied cell.",
  MESSAGE_NO_LETTER_SELECTED: "No letter has been selected.",
  MESSAGE_COLUMN_FULL: "That column is full.",
  MESSAGE_LINE_FULL: "There's no gap in that row or column to push into.",
  MESSAGE_USE_THE_ARROWS: "Use the arrows to slide a letter in from an edge.",

  LETTER_BLANK: "-",

  // From https://en.wikipedia.org/wiki/Letter_frequency#Relative_frequencies_of_letters_in_the_English_language.
  FREQUENCIES: [
    { letter: "A", frequency: 8167 },
    { letter: "B", frequency: 1492 },
    { letter: "C", frequency: 2782 },
    { letter: "D", frequency: 4253 },
    { letter: "E", frequency: 12702 },
    { letter: "F", frequency: 2228 },
    { letter: "G", frequency: 2015 },
    { letter: "H", frequency: 6094 },
    { letter: "I", frequency: 6966 },
    { letter: "J", frequency: 153 },
    { letter: "K", frequency: 772 },
    { letter: "L", frequency: 4025 },
    { letter: "M", frequency: 2406 },
    { letter: "N", frequency: 6749 },
    { letter: "O", frequency: 7507 },
    { letter: "P", frequency: 1929 },
    { letter: "Q", frequency: 95 },
    { letter: "R", frequency: 5987 },
    { letter: "S", frequency: 6327 },
    { letter: "T", frequency: 9056 },
    { letter: "U", frequency: 2758 },
    { letter: "V", frequency: 978 },
    { letter: "W", frequency: 2360 },
    { letter: "X", frequency: 150 },
    { letter: "Y", frequency: 1974 },
    { letter: "Z", frequency: 74 }
  ],

  GOLD: "gold",
  SILVER: "silver",
  BRONZE: "bronze",

  MODAL_NONE: "none",
  MODAL_ANNOUNCEMENT: "announcement",
  MODAL_FEEDBACK: "feedback",
  MODAL_LEADERBOARD: "leaderboard",
  MODAL_LOGIN: "login",
  MODAL_PROFILE: "profile",
  MODAL_RULES: "rules",
  MODAL_SCHEDULE: "schedule",
  MODAL_SEASON_WELCOME: "season-welcome",

  MODE_NAME_ADJACENCY: "Adjacency Mode",
  MODE_NAME_DROP: "Drop Mode",
  MODE_NAME_INCREMENT: "Increment Mode",
  MODE_NAME_SLIDE: "Slide Mode",
  MODE_NAME_WRAPAROUND: "Wraparound Mode",
  MODE_NAME_STANDARD: "Standard Mode",

  // Expanded and bonus cells are board features rather than modes, but a game
  // explains them the same way it explains a mode, so they carry names of their
  // own. These match the schedule table's column headers, so the two places a
  // day is described call the same thing the same name.
  VARIANT_NAME_EXPANDED: "Expanded Cells",
  VARIANT_NAME_BONUS_CELLS: "Bonus Cells",
}

// Things every player should be told once, shown on the first daily they start
// while the announcement is live (`startDate` to `endDate`, both inclusive; no
// `endDate` means for good). The id is what an account records having seen, so
// it is never reused or renamed. An entry either names a `modal` of its own or
// carries a `title` and `paragraphs` for the generic announcement modal. This
// file is compiled for the server too, which has no JSX, so a paragraph is a
// string or a list of pieces, where a piece is a string or `{ strong: "..." }`.
//
// Retired ids, never to be reused: "seasons" (the seasons introduction, removed
// with its modal in 2026/10).
Constants.ANNOUNCEMENT_SEASON_1_FINAL_WEEK = "season-1-final-week";
Constants.ANNOUNCEMENT_SEASON_2 = "season-2";
Constants.ANNOUNCEMENTS = [
  // The best-five rule first had anything to drop in this week, and seasons are
  // new enough that a player may not know it exists. Season 1's final week;
  // Announcements.test.js holds the dates to the calendar.
  {
    id: Constants.ANNOUNCEMENT_SEASON_1_FINAL_WEEK,
    startDate: "2026-10-12",
    endDate: "2026-10-18",
    title: "It's the final week of Season 1!",
    paragraphs: [
      [
        "Note that your season score counts your ",
        { strong: "best five scores" },
        " for each day of the week. If you have a rough day or miss one, don't worry—you've got buffer."
      ],
      "Good luck and have fun!"
    ]
  },
  // The welcome to a new season: the last one's trophy winners, and the twist
  // this one introduces, named by its parameter so the modal can say which days
  // carry it. Live for the season's first week, while congratulating last
  // season's winners is still news; Announcements.test.js holds the dates to
  // the calendar.
  {
    id: Constants.ANNOUNCEMENT_SEASON_2,
    startDate: "2026-10-19",
    endDate: "2026-10-25",
    modal: Constants.MODAL_SEASON_WELCOME,
    season: 2,
    introduces: "wraparoundMode"
  }
];

Constants.SCORE_SPANS = [
  Constants.SCORE_SPAN_DAY,
  Constants.SCORE_SPAN_WEEK,
  Constants.SCORE_SPAN_SEASON
];

Constants.SCORE_RECENCIES = [
  Constants.SCORE_RECENCY_THIS,
  Constants.SCORE_RECENCY_LAST
];

// The six score periods, written as the matrix they are. Only the scoreboard's
// selectors read this table: a period id stays the single name for a period
// everywhere else, so the fetch, the titles, the day counts and the row
// rendering are untouched by the board being chosen two axes at a time.
Constants.SCORE_PERIODS_BY_SPAN = {
  [Constants.SCORE_SPAN_DAY]: {
    [Constants.SCORE_RECENCY_THIS]: Constants.SCORES_TODAY,
    [Constants.SCORE_RECENCY_LAST]: Constants.SCORES_YESTERDAY
  },
  [Constants.SCORE_SPAN_WEEK]: {
    [Constants.SCORE_RECENCY_THIS]: Constants.SCORES_THIS_WEEK,
    [Constants.SCORE_RECENCY_LAST]: Constants.SCORES_LAST_WEEK
  },
  [Constants.SCORE_SPAN_SEASON]: {
    [Constants.SCORE_RECENCY_THIS]: Constants.SCORES_THIS_SEASON,
    [Constants.SCORE_RECENCY_LAST]: Constants.SCORES_LAST_SEASON
  }
};

Constants.SCORE_SPAN_LABELS = {
  [Constants.SCORE_SPAN_DAY]: "Day",
  [Constants.SCORE_SPAN_WEEK]: "Week",
  [Constants.SCORE_SPAN_SEASON]: "Season"
};

// A recency is named for the span it belongs to, because "this" and "last" mean
// something different on each: they are today and yesterday on the day span. The
// season span is missing here on purpose -- its two options are named by season
// number, which only the board knows, so it names them itself.
Constants.SCORE_RECENCY_LABELS = {
  [Constants.SCORE_SPAN_DAY]: {
    [Constants.SCORE_RECENCY_THIS]: "Today",
    [Constants.SCORE_RECENCY_LAST]: "Yesterday"
  },
  [Constants.SCORE_SPAN_WEEK]: {
    [Constants.SCORE_RECENCY_THIS]: "This week",
    [Constants.SCORE_RECENCY_LAST]: "Last week"
  }
};

// The axis's own words, for an option a span cannot name: the season before
// Season 1 has no number to be called by, so the option that would hold it reads
// "Last" and sits there disabled.
Constants.SCORE_RECENCY_NAMES = {
  [Constants.SCORE_RECENCY_THIS]: "This",
  [Constants.SCORE_RECENCY_LAST]: "Last"
};

// A period covering today, for a player who has not earned the right to see it.
// It carries no scores at all: the boards for today are fetched only once the
// daily is played, so there is nothing here to withhold in the first place --
// which is the point, since a shape holding scores it declined to show would be
// one bad render away from showing them. Distinct from `unavailable`, which is a
// period that does not exist yet rather than one that has not been earned.
Constants.LOCKED_PERIOD = { locked: true };

Constants.SCORE_PERIOD_POSSESSIVES = {
  [Constants.SCORES_TODAY]: "today's",
  [Constants.SCORES_THIS_WEEK]: "this week's",
  [Constants.SCORES_THIS_SEASON]: "this season's"
};

// How many days each score period covers. Periods not listed here are single
// days, and the scoreboard renders them without a per-day breakdown.
Constants.SCORE_PERIOD_DAY_COUNTS = {
  [Constants.SCORES_THIS_WEEK]: 7,
  [Constants.SCORES_LAST_WEEK]: 7,
  [Constants.SCORES_THIS_SEASON]: Constants.SEASON_LENGTH_DAYS,
  [Constants.SCORES_LAST_SEASON]: Constants.SEASON_LENGTH_DAYS
};

Constants.ACCOLADE_COLORS = [
  Constants.GOLD,
  Constants.SILVER,
  Constants.BRONZE
]

Constants.SCORE_DESCRIPTORS = [
  "top",
  "second-highest",
  "third-highest"
]

// Where players write to: the feedback modal and every error that asks to be
// reported.
Constants.FEEDBACK_EMAIL = "max.woghiren+10w@gmail.com";
Constants.ERROR_GAME_NOT_RECORDED = `Your board couldn't be saved, so this score won't reach the leaderboard.\nPlease email ${Constants.FEEDBACK_EMAIL} to let me know this happened!`;
Constants.ERROR_NO_ATTEMPT_FOUND = `No attempt found.\nPlease email ${Constants.FEEDBACK_EMAIL} to let me know this happened!`;
Constants.ERROR_MULTIPLE_ATTEMPTS_FOUND = `Multiple attempts found.\nPlease email ${Constants.FEEDBACK_EMAIL} to let me know this happened!`;

// Chat. Messages are kept for a week after they are posted and then deleted by
// Mongo itself (a TTL index on `createdAt`), so changing the retention means a
// `collMod` on the live index as well as this number. The length and the
// cooldown are enforced by the server; the client only mirrors them.
Constants.CHAT_MAX_LENGTH = 280;
Constants.CHAT_COOLDOWN_SECONDS = 3;
Constants.CHAT_POLL_INTERVAL_MS = 5000;
Constants.CHAT_MESSAGE_LIMIT = 200;
Constants.CHAT_RETENTION_DAYS = 7;
Constants.ERROR_CHAT_INVALID = `Messages must be between 1 and ${Constants.CHAT_MAX_LENGTH} characters long.`;

// What a streak reads as before one has been fetched, and what an account with
// no games has. Shared so the header, the profile and a failed request all show
// the same thing rather than three shapes of nothing.
Constants.NO_STREAKS = {
  current: 0,
  longest: 0
}

export default Constants;
